import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  Spinner,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { updateChatSessionHeaders } from "services/AgentChatService";
import showToast from "hooks/useCustomToast";
import { useAnalystChat } from "context/AnalystChatContext";

interface EditChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string | null;
  initialTitle?: string;
  initialDescription?: string;
  onSuccess?: () => void | Promise<void>;
}

const EditChatModal: React.FC<EditChatModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  initialTitle = "",
  initialDescription = "",
  onSuccess,
}) => {
  const [editTitleValue, setEditTitleValue] = useState("");
  const [editDescriptionValue, setEditDescriptionValue] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const { currentSessionHeader, setCurrentSessionHeader } = useAnalystChat();

  // Reset form when modal opens/closes or initial values change
  useEffect(() => {
    if (isOpen) {
      setEditTitleValue(initialTitle);
      setEditDescriptionValue(initialDescription);
    } else {
      setEditTitleValue("");
      setEditDescriptionValue("");
    }
  }, [isOpen, initialTitle, initialDescription]);

  // Handle save title and description
  const handleUpdateChat = async () => {
    if (!sessionId || !editTitleValue.trim() || !editDescriptionValue.trim()) return;

    setIsUpdating(true);
    try {
      const success = await updateChatSessionHeaders(
        [sessionId],
        editTitleValue.trim(),
        editDescriptionValue.trim()
      );
      if (success) {
        showToast("Chat updated successfully", "", "success");
        onClose();
        // Call onSuccess callback if provided (e.g., to refresh the list)
        if (onSuccess) {
          await onSuccess();
        }
        setCurrentSessionHeader({ ...currentSessionHeader, name: editTitleValue.trim(), description: editDescriptionValue.trim() });
      }
    } catch (error) {
      showToast("Failed to update chat", "", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={!isUpdating}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update Chat Details</ModalHeader>
        {!isUpdating && <ModalCloseButton />}
        <ModalBody>
          {isUpdating ? (
            <VStack spacing={4} py={6}>
              <Spinner size="lg" color="purple.500" thickness="3px" />
              <Text color="gray.600" fontSize="md">Updating title...</Text>
            </VStack>
          ) : (
            <VStack spacing={4} py={6}>
              <FormControl>
                <FormLabel>Chat Title</FormLabel>
                <Input
                  placeholder="Enter a new title..."
                  value={editTitleValue}
                  onChange={(e) => setEditTitleValue(e.target.value)}
                  autoFocus
                />
              </FormControl>
              <FormControl>
                <FormLabel>Chat Description</FormLabel>
                <Textarea
                  placeholder="Enter a description..."
                  value={editDescriptionValue}
                  onChange={(e) => setEditDescriptionValue(e.target.value)}
                  resize="none"
                  rows={6}
                  autoFocus
                />
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        {!isUpdating && (
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              isDisabled={!editTitleValue.trim() || !editDescriptionValue.trim()}
              onClick={handleUpdateChat}
            >
              Update
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default EditChatModal;
